/**
 * Ticket 114.3 — Typed API client for the Demos module.
 *
 * Covers all endpoints exposed in Phases 1+2:
 *   - Demo CRUD (GET list/detail, POST, PATCH, DELETE)
 *   - Catalog reads (countries, industries, themes, stats)
 *   - Brand defaults (GET/PUT)
 *
 * All functions return the Axios response data directly (typed).
 * Error handling is left to the caller / React Query onError.
 */

import { apiClient } from '@/lib/apiClient';

// ------------------------------------------------------------------ //
// Shared types
// ------------------------------------------------------------------ //

export type DemoStatus =
  | 'DRAFT'
  | 'AWAITING_REFINEMENT'
  | 'READY_TO_SEND'
  | 'PUBLISHED'
  | 'ARCHIVED';

export type DemoTheme = 'sunburst' | 'editorial' | 'quantum' | 'aurora';

export interface Demo {
  id: string;
  tenantId: string;
  slug: string;
  country: string;
  industryKey: string;
  businessName: string;
  /** JSON string — parse as needed */
  audience: string;
  theme: DemoTheme;
  /** JSON string — parse as needed */
  content: string;
  status: DemoStatus;
  opportunityId: string | null;
  shareToken: string | null;
  isPublic: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface DemoCreateRequest {
  country: string;
  industryKey: string;
  businessName: string;
  audience?: string;
  theme?: DemoTheme;
  opportunityId?: string | null;
  /** Initial content as JSON string; built by buildInitialContentMock in this phase. */
  content?: string;
}

export interface DemoUpdateRequest {
  content?: string;
  theme?: DemoTheme;
  status?: DemoStatus;
  audience?: string;
  businessName?: string;
  opportunityId?: string | null;
}

export interface DemoCountry {
  code: string;
  name: string;
  defaultCurrency: string;
  defaultLanguage: string;
  defaultThemes: unknown;
  brandVocabulary: unknown;
  isActive: boolean;
}

export interface DemoIndustry {
  id: string;
  country: string;
  key: string;
  name: string;
  tagline: string;
  icon: string | null;
  accentColor: string | null;
  defaultTheme: string | null;
  painPoints: unknown;
  valueProps: unknown;
  agents: unknown;
  /** JSON string describing the sections schema */
  contentSchema: unknown;
  /** JSON string with sample business data */
  sampleBusiness: unknown;
  isSystem: boolean;
  isActive: boolean;
  isCustom: boolean;
}

export interface DemoThemeCatalog {
  key: string;
  label: string;
}

export interface DemoBrandDefaults {
  tenantId: string;
  primaryColor: string | null;
  secondaryColor: string | null;
  logoUrl: string | null;
  /** JSON array string, e.g. '["sunburst","aurora"]' */
  allowedThemes: string;
}

export interface DemoBrandDefaultsUpdateRequest {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  logoUrl?: string | null;
  /** Array of theme keys sent to the backend as a JSON array, e.g. ["sunburst","aurora"] */
  allowedThemes?: string[];
}

// ------------------------------------------------------------------ //
// Demo CRUD
// ------------------------------------------------------------------ //

export async function listDemos(): Promise<Demo[]> {
  const { data } = await apiClient.get<Demo[]>('/demos');
  return data;
}

export async function getDemo(id: string): Promise<Demo> {
  const { data } = await apiClient.get<Demo>(`/demos/${id}`);
  return data;
}

export async function createDemo(request: DemoCreateRequest): Promise<Demo> {
  const { data } = await apiClient.post<Demo>('/demos', request);
  return data;
}

export async function updateDemo(id: string, request: DemoUpdateRequest): Promise<Demo> {
  const { data } = await apiClient.patch<Demo>(`/demos/${id}`, request);
  return data;
}

export async function deleteDemo(id: string): Promise<void> {
  await apiClient.delete(`/demos/${id}`);
}

// ------------------------------------------------------------------ //
// Catalog reads
// ------------------------------------------------------------------ //

export async function listDemoCountries(): Promise<DemoCountry[]> {
  const { data } = await apiClient.get<DemoCountry[]>('/demos/countries');
  return data;
}

export async function listDemoIndustries(params?: {
  country?: string;
  locale?: string;
}): Promise<DemoIndustry[]> {
  const { data } = await apiClient.get<DemoIndustry[]>('/demos/industries', { params });
  return data;
}

export async function getDemoIndustry(
  country: string,
  key: string,
  locale = 'es',
): Promise<DemoIndustry> {
  const { data } = await apiClient.get<DemoIndustry>(`/demos/industries/${country}/${key}`, {
    params: { locale },
  });
  return data;
}

export async function listDemoThemes(): Promise<DemoThemeCatalog[]> {
  const { data } = await apiClient.get<DemoThemeCatalog[]>('/demos/themes');
  return data;
}

export async function getDemoIndustryStats(
  country: string,
  key: string,
): Promise<Record<string, string>> {
  const { data } = await apiClient.get<Record<string, string>>(
    `/demos/industries/${country}/${key}/stats`,
  );
  return data;
}

// ------------------------------------------------------------------ //
// Brand defaults (Tenant Admin)
// ------------------------------------------------------------------ //

export async function getDemoBrandDefaults(): Promise<DemoBrandDefaults> {
  const { data } = await apiClient.get<DemoBrandDefaults>('/demos/brand-defaults');
  return data;
}

export async function updateDemoBrandDefaults(
  request: DemoBrandDefaultsUpdateRequest,
): Promise<DemoBrandDefaults> {
  const { data } = await apiClient.put<DemoBrandDefaults>('/demos/brand-defaults', request);
  return data;
}

// ------------------------------------------------------------------ //
// Phase 5 — Publish snapshot + share-token management
// ------------------------------------------------------------------ //

export interface PublishResult {
  publishedPath: string;
  publicUrl: string;
}

export interface ShareTokenResult {
  shareToken: string;
  shareableUrl: string;
}

/**
 * Trigger the server-side publish snapshot for a demo.
 * Renders the Thymeleaf template to disk, sets status = PUBLISHED.
 * Returns the local path and the public URL.
 * Requires demo.publish permission.
 */
export async function publishDemo(demoId: string): Promise<PublishResult> {
  const { data } = await apiClient.post<PublishResult>(`/demos/${demoId}/publish`);
  return data;
}

/**
 * Rotate (or generate) the share token for a demo.
 * Returns the new token and the full shareable URL (includes ?token=).
 * Requires demo.refine.advanced OR demo.publish.
 */
export async function rotateShareToken(demoId: string): Promise<ShareTokenResult> {
  const { data } = await apiClient.post<ShareTokenResult>(`/demos/${demoId}/share-token`);
  return data;
}

/**
 * Revoke the share token for a demo. After revocation, ?token= links
 * will return 404 for private demos.
 * Requires demo.refine.advanced OR demo.publish.
 */
export async function revokeShareToken(demoId: string): Promise<void> {
  await apiClient.delete(`/demos/${demoId}/share-token`);
}

/**
 * Toggle the is_public flag.
 * Requires demo.publish.
 */
export async function setDemoVisibility(demoId: string, isPublic: boolean): Promise<Demo> {
  const { data } = await apiClient.patch<Demo>(`/demos/${demoId}/visibility`, { isPublic });
  return data;
}

// ------------------------------------------------------------------ //
// Phase 4 — LLM content generation + section refinement
// ------------------------------------------------------------------ //

/**
 * Triggers LLM-backed initial content generation for a demo.
 * If enriched = true (default), the prompt includes corpus context
 * from prior demos in the same industry.
 *
 * Returns the updated content envelope (schemaVersion + sections).
 * On 502, the caller should fall back to buildInitialContentMock().
 */
export async function generateDemoContent(demoId: string, enriched = true): Promise<unknown> {
  const { data } = await apiClient.post<unknown>(
    `/demos/${demoId}/generate`,
    null,
    { params: { enriched } },
  );
  return data;
}

/**
 * Refines a single section of a demo using the LLM.
 *
 * Returns the updated section node.
 */
export async function refineDemoSection(
  demoId: string,
  sectionKey: string,
  instruction: string,
): Promise<unknown> {
  const { data } = await apiClient.post<unknown>(
    `/demos/${demoId}/sections/${sectionKey}/refine`,
    { instruction },
  );
  return data;
}

// ------------------------------------------------------------------ //
// Phase 6 — Hand-off + sharing (114.6)
// ------------------------------------------------------------------ //

export interface DemoHandoffResponse {
  demoId: string;
  status: DemoStatus;
  assignedTo: string;
}

export interface DemoShareEmailRequest {
  recipientEmails: string[];
  message?: string | null;
}

export interface DemoShareWhatsAppRequest {
  opportunityContactId: string;
  message?: string | null;
}

/**
 * Transition the demo to AWAITING_REFINEMENT and assign it to the next
 * available PRE_SALES user (round-robin by fewest open demos).
 * Emits a DEMO_HANDOFF_REQUESTED in-app notification.
 * Requires demo.create.basic.
 */
export async function handoffDemo(demoId: string): Promise<DemoHandoffResponse> {
  const { data } = await apiClient.post<DemoHandoffResponse>(`/demos/${demoId}/handoff`);
  return data;
}

/**
 * Share the demo via email to one or more recipients.
 * Logs a DEMO_SHARED activity event on the linked opportunity (if any).
 * Requires demo.create.basic or demo.publish.
 */
export async function shareDemoViaEmail(
  demoId: string,
  request: DemoShareEmailRequest,
): Promise<void> {
  await apiClient.post(`/demos/${demoId}/share/email`, request);
}

/**
 * Share the demo via WhatsApp to the given contact's phone number.
 * Logs a DEMO_SHARED activity event on the linked opportunity (if any).
 * Requires demo.create.basic or demo.publish.
 */
export async function shareDemoViaWhatsApp(
  demoId: string,
  request: DemoShareWhatsAppRequest,
): Promise<void> {
  await apiClient.post(`/demos/${demoId}/share/whatsapp`, request);
}

/**
 * List demos linked to a specific opportunity.
 * Requires demo.read.
 */
export async function listDemosByOpportunity(opportunityId: string): Promise<Demo[]> {
  const { data } = await apiClient.get<Demo[]>('/demos', { params: { opportunityId } });
  return data;
}

// ------------------------------------------------------------------ //
// Phase 7 — Analytics funnel (114.7)
// ------------------------------------------------------------------ //

export interface DemoFunnel {
  created: number;
  shared: number;
  ctaClicked: number;
  oppAdvanced: number;
  createdToSharedPct: number;
  sharedToCtaPct: number;
  ctaToOppAdvancedPct: number;
}

export interface DemoFunnelFilters {
  from?: string;
  to?: string;
  rep?: string;
  industry?: string;
  theme?: string;
}

/**
 * Fetch the demo engagement funnel for the SALES_MANAGER view.
 * Filters are optional ISO date strings (from/to), rep UUID, industry key, theme key.
 * Requires demo.read permission.
 */
export async function fetchDemoAnalyticsFunnel(filters?: DemoFunnelFilters): Promise<DemoFunnel> {
  const params: Record<string, string> = {};
  if (filters?.from) params.from = filters.from;
  if (filters?.to) params.to = filters.to;
  if (filters?.rep) params.rep = filters.rep;
  if (filters?.industry) params.industry = filters.industry;
  if (filters?.theme) params.theme = filters.theme;
  const { data } = await apiClient.get<DemoFunnel>('/demos/analytics/funnel', { params });
  return data;
}

// ------------------------------------------------------------------ //
// Static mock content generator
//
// Phase 3 builds the initial demo content directly from the industry's
// sample_business + content_schema. Phase 4 will replace or chain after
// this function with a real LLM call to DemoContentService.generateInitial.
// ------------------------------------------------------------------ //

export interface ContentSection {
  key: string;
  type: string;
  headline?: string;
  subheadline?: string;
  body?: string;
  items?: unknown[];
  imageUrl?: string;
  status: 'empty' | 'needs-edit' | 'approved';
}

export interface DemoContent {
  schemaVersion: 1;
  sections: Record<string, ContentSection>;
}

/**
 * buildInitialContentMock — Phase 3 static content builder.
 *
 * Constructs the initial content object for a new demo from the
 * industry's sample_business and content_schema. Returns a JSON string
 * suitable for DemoCreateRequest.content / DemoUpdateRequest.content.
 *
 * Phase 4 will either replace this function or wrap it in a
 * server-side DemoContentService.generateInitial call.
 */
export function buildInitialContentMock(industry: DemoIndustry): string {
  const schema = (industry.contentSchema ?? {}) as {
    sections?: Array<{ key: string; type: string }>;
  };
  const sample = (industry.sampleBusiness ?? {}) as Record<string, unknown>;

  const sections: Record<string, ContentSection> = {};

  const schemaSections = schema.sections ?? [
    { key: 'hero', type: 'hero' },
    { key: 'menu', type: 'list' },
    { key: 'offers', type: 'cards' },
    { key: 'testimonials', type: 'quotes' },
    { key: 'contact', type: 'contact' },
  ];

  for (const sectionDef of schemaSections) {
    const s = sample[sectionDef.key] as Record<string, unknown> | undefined;
    sections[sectionDef.key] = {
      key: sectionDef.key,
      type: sectionDef.type,
      headline: (s?.headline as string | undefined) ?? '',
      subheadline: (s?.subheadline as string | undefined) ?? '',
      body: (s?.body as string | undefined) ?? '',
      items: (s?.items as unknown[] | undefined) ?? [],
      imageUrl: (s?.imageUrl as string | undefined) ?? undefined,
      status: s ? 'needs-edit' : 'empty',
    };
  }

  const content: DemoContent = {
    schemaVersion: 1,
    sections,
  };

  return JSON.stringify(content);
}

// ------------------------------------------------------------------ //
// Phase 8 — Stat refresh admin (PLATFORM_ADMIN only)
// ------------------------------------------------------------------ //

export interface RefreshResult {
  country: string;
  industryKey: string;
  added: number;
  updated: number;
  skipped: number;
  pending: number;
  errorMessage: string | null;
}

export interface StatRefreshPendingRow {
  id: string;
  country: string;
  industryKey: string;
  statKey: string;
  proposedValue: string;
  currentValue: string | null;
  status: string;
  createdAt: string;
}

export interface StatRefreshConfig {
  enabled: boolean;
  requireApproval: boolean;
  webSearchEnabled: boolean;
}

/**
 * Trigger a one-shot stat refresh for a specific (country, industryKey).
 * Requires demo.catalog.manage (PLATFORM_ADMIN).
 */
export async function runStatRefreshOnce(
  country: string,
  industryKey: string,
): Promise<RefreshResult> {
  const { data } = await apiClient.post<RefreshResult>(
    `/admin/demos/stat-refresh/run-once`,
    null,
    { params: { country, industryKey } },
  );
  return data;
}

/**
 * List pending stat refresh rows awaiting PLATFORM_ADMIN approval.
 */
export async function listPendingStatRefreshes(): Promise<StatRefreshPendingRow[]> {
  const { data } = await apiClient.get<StatRefreshPendingRow[]>(
    `/admin/demos/stat-refresh/pending`,
  );
  return data;
}

/**
 * Approve a pending stat refresh row (applies it to demo_industry_stat).
 */
export async function approveStatRefresh(id: string): Promise<StatRefreshPendingRow> {
  const { data } = await apiClient.post<StatRefreshPendingRow>(
    `/admin/demos/stat-refresh/pending/${id}/approve`,
  );
  return data;
}

/**
 * Reject a pending stat refresh row (discards it).
 */
export async function rejectStatRefresh(id: string): Promise<StatRefreshPendingRow> {
  const { data } = await apiClient.post<StatRefreshPendingRow>(
    `/admin/demos/stat-refresh/pending/${id}/reject`,
  );
  return data;
}

/**
 * Get the current stat-refresh configuration (read-only, env-time values).
 */
export async function getStatRefreshConfig(): Promise<StatRefreshConfig> {
  const { data } = await apiClient.get<StatRefreshConfig>(
    `/admin/demos/stat-refresh/config`,
  );
  return data;
}
